import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * Custom hook for responsive component sizing throughout the app
 * 
 * @example
 * const { fontSize, spacing, buttonSize, iconSize } = useResponsiveSize();
 * return <Button sx={{ fontSize: fontSize.normal, padding: spacing.button }}>
 *          <Icon sx={{ fontSize: iconSize.small }} />
 *          Click Me
 *        </Button>
 */
const useResponsiveSize = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  // Font sizes with mobile optimization
  const fontSize = {
    tiny: isMobile ? '0.7rem' : '0.75rem',
    small: isMobile ? '0.8rem' : '0.875rem',
    normal: isMobile ? '0.9rem' : '1rem',
    medium: isMobile ? '1rem' : '1.25rem',
    large: isMobile ? '1.25rem' : '1.5rem',
    xl: isMobile ? '1.5rem' : '2rem',
    xxl: isMobile ? '2rem' : '2.5rem',
  };
  
  // Spacing values optimized for mobile
  const spacing = {
    xs: isMobile ? theme.spacing(0.5) : theme.spacing(1),
    sm: isMobile ? theme.spacing(1) : theme.spacing(2),
    md: isMobile ? theme.spacing(1.5) : theme.spacing(3),
    lg: isMobile ? theme.spacing(2) : theme.spacing(4),
    xl: isMobile ? theme.spacing(3) : theme.spacing(6),
    
    // Component-specific spacing
    card: isMobile ? theme.spacing(1.5) : theme.spacing(3),
    button: isMobile ? `${theme.spacing(0.5)} ${theme.spacing(1.5)}` : `${theme.spacing(1)} ${theme.spacing(2)}`,
    input: isMobile ? theme.spacing(1.5) : theme.spacing(2),
    section: isMobile ? theme.spacing(3) : theme.spacing(6),
  };
  
  // Component sizing
  const buttonSize = {
    small: {
      height: isMobile ? '28px' : '32px',
      fontSize: fontSize.tiny,
    },
    medium: {
      height: isMobile ? '36px' : '40px',
      fontSize: fontSize.small,
    },
    large: {
      height: isMobile ? '44px' : '48px',
      fontSize: fontSize.normal,
    },
  };
  
  // Icon sizing
  const iconSize = {
    small: isMobile ? '0.9rem' : '1.2rem',
    medium: isMobile ? '1.2rem' : '1.5rem',
    large: isMobile ? '1.5rem' : '2rem',
  };
  
  // Border radius
  const borderRadius = {
    small: isMobile ? '6px' : '8px',
    medium: isMobile ? '8px' : '12px',
    large: isMobile ? '12px' : '16px',
  };
  
  return {
    isMobile,
    isTablet,
    fontSize,
    spacing,
    buttonSize,
    iconSize,
    borderRadius,
  };
};

export default useResponsiveSize; 